import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import {
  CalendarClock,
  CheckCircle2,
  ListOrdered,
  XCircle,
  Zap,
} from "lucide-react";
import {
  FACEBOOK,
  INSTAGRAM,
  LINKEDIN,
  TIKTOK,
  YOUTUBE,
} from "../../../utils/constants";
import PlatformLogo from "../../../components/Common/PlatformLogo";
import { useTranslationApp } from "../../../lib/i18next";
import {
  AUTOMATIONS_HERO_DESCRIPTION,
  AUTOMATIONS_HERO_FAILED,
  AUTOMATIONS_HERO_NEXT,
  AUTOMATIONS_HERO_PUBLISHED,
  AUTOMATIONS_HERO_QUEUED,
  AUTOMATIONS_HERO_TITLE,
  AUTOMATIONS_NEXT_NONE,
} from "../../../i18n/keysTranslations";

const HERO_PLATFORMS = [INSTAGRAM, TIKTOK, LINKEDIN, FACEBOOK, YOUTUBE];

const HeroMetric = ({ icon: Icon, label, value, color, bgcolor, borderColor }) => (
  <Tooltip title={`${label}: ${value}`} arrow enterDelay={200}>
    <Chip
      size="small"
      icon={<Icon size={14} strokeWidth={2.25} aria-hidden />}
      label={value}
      sx={{
        height: 28,
        maxWidth: "100%",
        borderRadius: "8px",
        fontSize: 13,
        fontWeight: 600,
        color,
        bgcolor,
        border: "1px solid",
        borderColor,
        cursor: "default",
        "& .MuiChip-icon": {
          color: "inherit",
          ml: 1,
          mr: -0.25,
        },
        "& .MuiChip-label": {
          px: 1,
          py: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
      }}
    />
  </Tooltip>
);

const AutomationHeroCard = ({ businessName, metrics = {} }) => {
  const { t } = useTranslationApp();
  const nextValue = metrics.nextPostLabel || t(AUTOMATIONS_NEXT_NONE);

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "stretch",
        gap: { xs: 1.5, md: 2.5 },
        background: "linear-gradient(100deg, #f0f6ff, #f6f8fc)",
        border: "1px solid #e3ebf7",
        borderRadius: "14px",
        p: { xs: 2, sm: "20px 22px" },
        mb: 1.5,
        width: "100%",
        maxWidth: "100%",
        minWidth: 0,
        boxSizing: "border-box",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          display: "grid",
          placeItems: "center",
          flex: { xs: "0 0 56px", sm: "0 0 80px" },
          width: { xs: 56, sm: 80 },
          minHeight: { xs: 56, sm: 80 },
          alignSelf: "stretch",
          color: "#112238",
          background: "#b9d8ff",
          borderRadius: "14px",
        }}
      >
        <Zap size={36} fill="currentColor" />
      </Box>

      <Box
        sx={{
          flex: "1 1 220px",
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: 16, sm: 18 },
            fontWeight: 600,
            mb: 0.5,
            color: "#152033",
            overflowWrap: "anywhere",
          }}
        >
          {t(AUTOMATIONS_HERO_TITLE)}
        </Typography>
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: 1.45,
            color: "#58667b",
            overflowWrap: "anywhere",
          }}
        >
          {t(AUTOMATIONS_HERO_DESCRIPTION, { NAME_BUSINESS: businessName })}
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            mt: 1.25,
            alignItems: "center",
          }}
        >
          <HeroMetric
            icon={ListOrdered}
            label={t(AUTOMATIONS_HERO_QUEUED)}
            value={metrics.queuedCount || 0}
            color="#1d4ed8"
            bgcolor="#eff6ff"
            borderColor="#bfdbfe"
          />
          <HeroMetric
            icon={CheckCircle2}
            label={t(AUTOMATIONS_HERO_PUBLISHED)}
            value={metrics.publishedThisWeek || 0}
            color="#15803d"
            bgcolor="#f0fdf4"
            borderColor="#bbf7d0"
          />
          <HeroMetric
            icon={XCircle}
            label={t(AUTOMATIONS_HERO_FAILED)}
            value={metrics.failedCount || 0}
            color="#b91c1c"
            bgcolor="#fef2f2"
            borderColor="#fecaca"
          />
          <HeroMetric
            icon={CalendarClock}
            label={t(AUTOMATIONS_HERO_NEXT)}
            value={nextValue}
            color="#0369a1"
            bgcolor="#f0f9ff"
            borderColor="#bae6fd"
          />
        </Box>
      </Box>

      <Box
        sx={{
          ml: { xs: 0, lg: "auto" },
          display: { xs: "none", lg: "flex" },
          alignItems: "center",
          justifyContent: "center",
          gap: 1.25,
          flexWrap: "wrap",
          flex: "0 1 auto",
          minWidth: 0,
          maxWidth: "100%",
          px: 1.5,
          py: 1,
          borderRadius: "14px",
          background: "rgba(255,255,255,0.65)",
          border: "1px solid #e3ebf7",
          alignSelf: "stretch",
        }}
      >
        {HERO_PLATFORMS.map((platform) => (
          <PlatformLogo
            key={platform}
            platform={platform}
            width={32}
            height={32}
            alt={platform}
          />
        ))}
      </Box>
    </Paper>
  );
};

export default AutomationHeroCard;
